import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import i18n from 'i18next';
import { SUPPORTED_LANGUAGES, isSupportedLanguage } from '@/app/language';
import { isTransportError } from '@/app/api/loadResilience';
import SurveyDone from './SurveyDone';
import SurveyIntro from './SurveyIntro';
import SurveySection from './SurveySection';
import type { QuestionError } from './SurveyQuestion';
import { useSubmitSurveyResponseMutation } from './surveyApi';
import {
  clearDone,
  clearDraft,
  isDone,
  loadDraft,
  markDone,
  newSubmissionId,
  saveDraft,
} from './surveyDraft';
import {
  commitPendingDishes,
  hasRealAnswer,
  headingStyle,
  initialAnswers,
  localToday,
  missingRequired,
  pickText,
  pruneAnswers,
  serializeAnswers,
  surveyInkVars,
  visibleQuestionIds,
  visibleSections,
} from './surveyLogic';
import type { AnswerMap, AnswerState, PublicSurvey, SurveySchema } from './types';
import { useDocumentLanguage } from './useDocumentLanguage';

type Banner = 'emptyResponse' | 'rateLimited' | 'submitError' | 'invalidAnswers' | null;

interface Props {
  survey: PublicSurvey & { schema: SurveySchema; version: number };
  restaurantUuid: string;
}

function startLanguage(schema: SurveySchema, wanted: string | undefined): string {
  if (wanted && schema.languages.includes(wanted) && isSupportedLanguage(wanted)) return wanted;
  return isSupportedLanguage(schema.defaultLanguage) ? schema.defaultLanguage : 'fr';
}

/**
 * SC-1752 — the guest-facing survey. Opens in the survey's own default language
 * (never the browser's), keeps its language in local state, and does not
 * persist it as the site language.
 */
export default function SurveyPage({ survey, restaurantUuid }: Props) {
  const { schema, version, survey_uuid: surveyUuid, restaurant } = survey;
  const today = useMemo(() => localToday(), []);
  const draft = useMemo(() => loadDraft(surveyUuid), [surveyUuid]);

  const [lang, setLang] = useState(() => startLanguage(schema, draft?.language));
  const [answers, setAnswers] = useState<AnswerMap>(
    () => (draft ? pruneAnswers(schema, draft.answers) : initialAnswers(schema, today)),
  );
  const [step, setStep] = useState<number>(draft?.step ?? -1);
  const [submissionId, setSubmissionId] = useState(() => draft?.submissionId ?? newSubmissionId());
  const [phase, setPhase] = useState<'form' | 'done' | 'closed'>(() =>
    isDone(surveyUuid) ? 'done' : 'form',
  );
  const [justSent, setJustSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, QuestionError>>({});
  const [banner, setBanner] = useState<Banner>(null);
  const [errorTick, setErrorTick] = useState(0);
  const [barHeight, setBarHeight] = useState(160);
  const mainRef = useRef<HTMLElement>(null);
  // Set when a send ended without a verdict (no answer, timeout, 5xx): the hub may or may not have stored it.
  const unknownOutcome = useRef(false);
  const barRef = useRef<HTMLDivElement>(null);
  const [submit, { isLoading: sending }] = useSubmitSurveyResponseMutation();

  const t = useMemo(() => i18n.getFixedT(lang), [lang]);
  useDocumentLanguage(lang, `${t('survey.title')} — ${restaurant.store_name}`);
  const inkVars = useMemo(() => surveyInkVars(restaurant.theme), [restaurant.theme]);
  const heading = headingStyle(lang);
  const defaultLang = schema.defaultLanguage;

  const visible = useMemo(() => visibleQuestionIds(schema, answers), [schema, answers]);
  const screens = useMemo(() => visibleSections(schema, visible), [schema, visible]);
  const current = step >= 0 ? screens[Math.min(step, screens.length - 1)] : undefined;

  // The draft survives a refresh; it is removed on success.
  useEffect(() => {
    if (phase !== 'form') return;
    saveDraft(surveyUuid, { version, submissionId, answers, step, language: lang });
  }, [phase, surveyUuid, version, submissionId, answers, step, lang]);

  // Move keyboard / screen-reader focus to the new screen's heading.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  // First invalid question: bring it into view above the action bar and focus it.
  useEffect(() => {
    if (!errorTick) return;
    const el = mainRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView?.({ block: 'center', behavior: 'instant' });
  }, [errorTick]);

  // The fixed bar changes height (banner, wrapped text): content padding and
  // scroll-padding follow its measured height so nothing ends up underneath.
  const showBar = phase === 'form';
  useEffect(() => {
    const bar = barRef.current;
    if (!showBar || !bar || typeof ResizeObserver === 'undefined') return;
    const measure = () => setBarHeight(Math.ceil(bar.getBoundingClientRect().height));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [showBar]);
  useEffect(() => {
    const el = document.documentElement;
    el.style.scrollPaddingBottom = showBar ? `${barHeight + 16}px` : '';
    return () => {
      el.style.scrollPaddingBottom = '';
    };
  }, [showBar, barHeight]);

  const go = (next: number) => {
    setAnswers((prev) => commitPendingDishes(schema, prev, survey.dishes));
    setStep(next);
  };

  const change = (questionId: string, next: AnswerState) => {
    // The hub answers an already-stored id with the ORIGINAL response and ignores
    // new content, so an edit after an unknown outcome needs a fresh id.
    if (unknownOutcome.current) {
      unknownOutcome.current = false;
      setSubmissionId(newSubmissionId());
    }
    setAnswers((prev) => ({ ...prev, [questionId]: next }));
    setErrors((prev) => {
      if (!(questionId in prev)) return prev;
      const { [questionId]: _drop, ...rest } = prev;
      return rest;
    });
    setBanner(null);
  };

  const goToQuestion = (questionId: string) => {
    const idx = screens.findIndex((s) => s.questions.some((q) => q.id === questionId));
    if (idx >= 0) setStep(idx);
  };

  const send = async () => {
    setBanner(null);
    const committed = commitPendingDishes(schema, answers, survey.dishes);
    if (committed !== answers) setAnswers(committed);
    const missing = missingRequired(schema, committed);
    if (missing.length) {
      setErrors(Object.fromEntries(missing.map((id) => [id, 'required' as const])));
      goToQuestion(missing[0]);
      setErrorTick((n) => n + 1);
      return;
    }
    const list = serializeAnswers(schema, committed);
    if (!hasRealAnswer(list)) {
      setBanner('emptyResponse');
      return;
    }
    try {
      await submit({
        restaurantUuid,
        surveyUuid,
        body: { submission_id: submissionId, version, language: lang, website: '', answers: list },
      }).unwrap();
      markDone(surveyUuid);
      clearDraft(surveyUuid);
      unknownOutcome.current = false;
      setJustSent(true);
      setPhase('done');
    } catch (err) {
      const e = err as { status?: number | string; data?: { code?: string; fields?: { question_id: string; code: string }[] } };
      const unknown = isTransportError(e) || (typeof e.status === 'number' && e.status >= 500) || e.status === 'PARSING_ERROR';
      unknownOutcome.current = unknown;
      if (isTransportError(e)) return setBanner('submitError');
      if (e.status === 429) return setBanner('rateLimited');
      if (e.status === 410) return setPhase('closed');
      if (e.data?.code === 'EMPTY_RESPONSE') return setBanner('emptyResponse');
      if (e.data?.code === 'INVALID_ANSWERS' && e.data.fields?.length) {
        const marked: Record<string, QuestionError> = {};
        for (const f of e.data.fields) marked[f.question_id] = f.code === 'REQUIRED' ? 'required' : 'invalid';
        setErrors(marked);
        setBanner('invalidAnswers');
        goToQuestion(e.data.fields[0].question_id);
        setErrorTick((n) => n + 1);
        return;
      }
      setBanner('submitError');
    }
  };

  const answerAgain = () => {
    clearDone(surveyUuid);
    unknownOutcome.current = false;
    setSubmissionId(newSubmissionId());
    setAnswers(initialAnswers(schema, today));
    setErrors({});
    setBanner(null);
    setJustSent(false);
    setStep(-1);
    setPhase('form');
  };

  const outro = pickText(schema.outro, lang, defaultLang);
  const isLast = step >= screens.length - 1;
  const onIntro = phase === 'form' && step < 0;
  const btn = 'min-h-[48px] border-2 border-solid border-ink rounded-btn px-5 font-display text-lg shadow-stamp-sm disabled:opacity-60';
  const primary = `${btn} bg-mint text-[var(--on-primary)]`;
  const secondary = `${btn} bg-paper text-ink`;
  const languages = schema.languages.filter(isSupportedLanguage);
  const nativeLabel = (code: string) => SUPPORTED_LANGUAGES.find((l) => l.code === code)?.nativeLabel;

  return (
    <div
      className="min-h-[100dvh] bg-cream text-ink flex flex-col"
      style={inkVars as CSSProperties}
    >
      <header className="flex items-center justify-between gap-3 px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-1 min-w-0">
        <span className="font-display text-lg truncate min-w-0" style={heading}>
          {onIntro ? '' : restaurant.store_name}
        </span>
        {languages.length > 1 && phase === 'form' && (
          <div className="shrink-0">
            {languages.length > 2 ? (
              <>
                <label htmlFor="survey-lang" className="sr-only">
                  {t('survey.language')}
                </label>
                <select
                  id="survey-lang"
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="min-h-[44px] max-w-[9rem] border-2 border-solid border-ink rounded-btn-sm bg-paper text-ink px-2 font-mono text-sm"
                >
                  {languages.map((code) => (
                    <option key={code} value={code}>
                      {nativeLabel(code)}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <div role="group" aria-label={t('survey.language')} className="flex gap-1">
                {languages.map((code) => (
                  <button
                    key={code}
                    type="button"
                    aria-pressed={code === lang}
                    onClick={() => setLang(code)}
                    className={`min-h-[44px] min-w-[44px] border-2 border-solid border-ink rounded-btn-sm px-2 font-mono text-sm ${
                      code === lang ? 'bg-ink text-cream font-semibold' : 'bg-paper text-ink'
                    }`}
                  >
                    {nativeLabel(code)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      <main
        ref={mainRef}
        style={{ paddingBottom: phase === 'form' ? barHeight + 16 : 40 }}
        onFocusCapture={(e) => (e.target as HTMLElement).scrollIntoView?.({ block: 'nearest', behavior: 'instant' })}
        className="flex-1 px-4 max-w-xl w-full mx-auto box-border"
      >
        {phase === 'closed' && (
          <p className="font-display text-2xl pt-10 text-center" style={heading}>
            {t('survey.closed')}
          </p>
        )}
        {phase === 'done' && (
          <SurveyDone outro={outro} offerAgain={!justSent} onAgain={answerAgain} t={t} lang={lang} />
        )}
        {onIntro && (
          <SurveyIntro
            storeName={restaurant.store_name}
            logo={restaurant.logo}
            intro={pickText(schema.intro, lang, defaultLang)}
            t={t}
            lang={lang}
          />
        )}
        {phase === 'form' && current && (
          <>
            <p className="font-mono text-sm text-ink-mute mb-3" aria-live="polite">
              {t('survey.progress', { current: Math.min(step, screens.length - 1) + 1, total: screens.length })}
            </p>
            <fieldset disabled={sending} className="contents border-0 p-0 m-0 min-w-0">
            <SurveySection
              ref={headingRef}
              section={current.section}
              questions={current.questions}
              answers={answers}
              errors={errors}
              lang={lang}
              defaultLang={defaultLang}
              dishes={survey.dishes}
              today={today}
              t={t}
              onChange={change}
            />
            </fieldset>
          </>
        )}
      </main>

      {phase === 'form' && (
        <div
          ref={barRef}
          className="fixed inset-x-0 bottom-0 z-10 border-t-2 border-solid border-ink bg-paper px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="mx-auto flex max-w-xl flex-col gap-2">
            {banner && (
              <p
                role="alert"
                className="m-0 border-2 border-solid border-sale-red rounded-btn-sm p-2 font-mono text-sm font-semibold text-ink"
              >
                <span aria-hidden="true" className="text-sale-red">⚠ </span>
                {t(`survey.${banner}`)}
              </p>
            )}
            {step < 0 ? (
              <button
                type="button"
                onClick={() => go(screens.length > 0 ? 0 : -1)}
                className={primary}
                style={heading}
              >
                {t('survey.start')}
              </button>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <button type="button" disabled={sending} onClick={() => go(step - 1)} className={secondary} style={heading}>
                    {t('survey.previous')}
                  </button>
                  {!isLast && (
                    <button type="button" disabled={sending} onClick={() => go(step + 1)} className={primary} style={heading}>
                      {t('survey.next')}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  disabled={sending}
                  onClick={send}
                  className={isLast ? primary : secondary}
                  style={heading}
                >
                  {sending ? t('survey.sending') : t('survey.submit')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
